import React, { FC } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { StorageDocumentLink } from '../../../../components/storage-document-link/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useFormat } from '../../../../hooks/index';
import { safeCapTypeValuationCapTitle } from '../../../../types/safes.types';
import { downloadCompanyStorageFile } from '../../../../utils/company-storage-url.util';
import { ChildrenFormProps } from '../modals.types';

export type FinalStepProps = ChildrenFormProps;

export const FinalStep: FC<FinalStepProps> = ({
  companyId,
  handleCloseModal,
  nextFormStep,
  formData,
  prevFormStep,
  isLoading,
}) => {
  const { stepOne, stepTwo, stepThree } = formData();
  const { format } = useFormat();
  const handleDocumentClick = (fileLink: string) => {
    void downloadCompanyStorageFile(companyId, fileLink);
  };
  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <span className="px-4 text-sm font-[550] text-gray-700">
            <AppFormattedMessage id="GENERAL" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <SummaryItem
              className="pt-0"
              title={<AppFormattedMessage id="SHAREHOLDER" />}
              value={
                <Tooltip content={stepOne?.stakeholder?.fullName}>
                  {stepOne?.stakeholder?.fullName}
                </Tooltip>
              }
            />

            <SummaryItem
              title={<AppFormattedMessage id="INVESTMENT" />}
              value={stepOne?.investment && Number(stepOne?.investment).toLocaleString('en-US')}
            />
            <SummaryItem
              className="pb-0"
              title={<AppFormattedMessage id="ISSUE_DATE" />}
              value={format(stepOne?.issueDate, 'dd/MM/yyyy')}
            />
          </div>
        </div>

        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <span className="px-4 text-sm font-[550] text-gray-700">
            <AppFormattedMessage id="CAP_OR_DISCOUNT_DETAILS" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <SummaryItem
              className="pt-0"
              title={
                <AppFormattedMessage
                  id={
                    stepTwo?.cap?.capValue && stepTwo.cap.capType
                      ? safeCapTypeValuationCapTitle[stepTwo.cap.capType]
                      : 'CAP'
                  }
                />
              }
              value={
                stepTwo?.cap?.capValue
                  ? Number(stepTwo?.cap?.capValue).toLocaleString('en-US')
                  : '-'
              }
            />

            <SummaryItem
              className="pb-0"
              title={<AppFormattedMessage id="DISCOUNT" />}
              value={
                stepTwo?.discount?.discountValue ? `${stepTwo?.discount?.discountValue}%` : '-'
              }
            />
          </div>
        </div>

        {stepTwo?.floor?.enabled && (
          <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
            <span className="px-4 text-sm font-[550] text-gray-700">
              <AppFormattedMessage id="FLOOR_AND_MATURITY_DATE" />
            </span>
            <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
              <SummaryItem
                className="pt-0"
                title={<AppFormattedMessage id="FLOOR" />}
                value={
                  stepTwo?.floor?.floorValue
                    ? Number(stepTwo?.floor?.floorValue).toLocaleString('en-US')
                    : '-'
                }
              />
              <SummaryItem
                className="pb-0"
                title={<AppFormattedMessage id="MATURITY_DATE" />}
                value={
                  stepTwo?.floor?.maturityDate
                    ? format(stepTwo?.floor?.maturityDate, 'dd/MM/yyyy')
                    : '-'
                }
              />
            </div>
          </div>
        )}

        <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white">
          <div className={'flex gap-3 text-nowrap px-4 py-2'}>
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              <AppFormattedMessage id="DOCUMENTS" />:
            </span>
            <div className="flex max-w-[220px] flex-col gap-2">
              {stepThree?.files?.some(({ docLink }) => docLink) ? (
                stepThree.files.map(
                  ({ doc, docLink }) =>
                    docLink && (
                      <StorageDocumentLink
                        key={docLink + doc.name}
                        label={doc.name}
                        link={docLink}
                        onDocumentClick={handleDocumentClick}
                      />
                    ),
                )
              ) : (
                <span className="truncate text-xs font-[450] text-gray-700">-</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white">
          <div className="flex w-full gap-3 px-4 py-2">
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              {<AppFormattedMessage id="ADDITIONAL_NOTES" />}:
            </span>
            <div className="max-w-[250px] break-words text-xs font-[450] text-gray-700">
              {stepThree?.additionalNotes || '-'}
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
            onClick={prevFormStep}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>

          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            isLoading={isLoading}
            onClick={nextFormStep}
            type="button"
          >
            <AppFormattedMessage id="ADD_SAFE" />
          </Button>
        </div>
      </div>
    </>
  );
};
